import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export interface ReportSettingsDto {
  email_header_html: string;
  email_footer_html: string;
  signature_name: string;
  signature_title: string;
  signature_block_html: string;
  contact_email: string;
  contact_phone: string;
  calendly_url: string;
  report_link_ttl_hours: number;
  confidentiality_notice: string;
  updated_at: string;
}

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export const getReportSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ settings: ReportSettingsDto }> => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("report_settings")
      .select(
        "email_header_html,email_footer_html,signature_name,signature_title,signature_block_html,contact_email,contact_phone,calendly_url,report_link_ttl_hours,confidentiality_notice,updated_at",
      )
      .eq("id", "default")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("report_settings row missing");
    return { settings: data as ReportSettingsDto };
  });

const UpdateSchema = z.object({
  email_header_html: z.string().max(8000),
  email_footer_html: z.string().max(8000),
  signature_name: z.string().trim().min(1).max(200),
  signature_title: z.string().trim().max(200),
  signature_block_html: z.string().max(8000),
  contact_email: z.string().trim().email().max(200),
  contact_phone: z.string().trim().max(60),
  calendly_url: z.string().trim().url().max(500),
  report_link_ttl_hours: z.number().int().min(1).max(168),
  confidentiality_notice: z.string().max(2000),
});

export const updateReportSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => UpdateSchema.parse(data))
  .handler(async ({ context, data }): Promise<{ ok: true }> => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("report_settings")
      .upsert({ id: "default", ...data, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export interface ReportAccessLogEntry {
  id: string;
  accessed_at: string;
  source: "email" | "portal" | "admin";
  ip: string | null;
  user_agent: string | null;
  signed_url_expires_at: string | null;
}

export const listReportAccessLog = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ order_id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }): Promise<{ entries: ReportAccessLogEntry[] }> => {
    await assertAdmin(context.userId);
    const { data: rows, error } = await supabaseAdmin
      .from("report_access_log")
      .select("id,accessed_at,source,ip,user_agent,signed_url_expires_at")
      .eq("order_id", data.order_id)
      .order("accessed_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { entries: (rows ?? []) as ReportAccessLogEntry[] };
  });